/**
 * VarianceBreakdown Component
 *
 * Table/list showing weeks with highest prediction variance:
 * - Weeks where predictions differed most from actuals
 * - Visual indicator showing relative variance magnitude
 * - Predicted vs actual values with variance
 *
 * US-FORECAST-CALIBRATION Step 8
 */

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { parseDateString } from '@/utils/dateUtils'

/**
 * Get variance colour based on percentage
 * @param {number} variancePct - Variance as percentage
 * @returns {string} Tailwind colour class
 */
function getVarianceColor(variancePct) {
  const abs = Math.abs(variancePct)
  if (abs < 15) return 'bg-emerald-500'
  if (abs < 30) return 'bg-amber-500'
  return 'bg-red-500'
}

/**
 * Format kg value for display
 * @param {number|null} value - Value in kg
 * @returns {string} Formatted string
 */
function formatKg(value) {
  if (value == null) return '-'
  return `${value.toFixed(1)} kg`
}

/**
 * Format week date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "Week of 6 Jan")
 */
function formatWeekLabel(dateString) {
  if (!dateString) return 'Unknown week'
  const date = parseDateString(dateString)
  return `Week of ${date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })}`
}

/**
 * Format percentage for display
 * @param {number|null} value - Percentage value
 * @returns {string} Formatted string
 */
function formatVariance(value) {
  if (value == null) return '-'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

/**
 * Individual variance row component
 */
function VarianceRow({ week, maxVariance }) {
  const {
    prediction_week,
    predicted_kg,
    actual_kg,
    variance_kg,
    variance_pct,
    variance_abs
  } = week

  // Calculate bar width as percentage of max variance
  const barWidth = maxVariance > 0 ? (variance_abs / maxVariance) * 100 : 0

  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between gap-4">
        {/* Week label and variance bar */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {formatWeekLabel(prediction_week)}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  getVarianceColor(variance_pct)
                )}
                style={{ width: `${Math.min(barWidth, 100)}%` }}
              />
            </div>
            <span className={cn(
              'text-xs font-medium w-16 text-right',
              variance_pct > 0 ? 'text-amber-600' : 'text-blue-600'
            )}>
              {formatVariance(variance_pct)}
            </span>
          </div>
        </div>

        {/* Values */}
        <div className="flex items-center gap-4 text-sm">
          <div className="text-right">
            <p className="text-muted-foreground text-xs">Predicted</p>
            <p className="font-medium">{formatKg(predicted_kg)}</p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground text-xs">Actual</p>
            <p className="font-medium">{formatKg(actual_kg)}</p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground text-xs">Variance</p>
            <p className={cn(
              'font-medium',
              variance_kg > 0 ? 'text-amber-600' : 'text-blue-600'
            )}>
              {variance_kg > 0 ? '+' : ''}{formatKg(variance_kg)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * VarianceBreakdown - Weekly variance table
 *
 * @param {Object} props
 * @param {Array} props.weeks - Weekly variance data (prop name kept as 'customers' for backwards compat)
 *   Each item: { prediction_week, predicted_kg, actual_kg, variance_kg, variance_pct, variance_abs }
 * @param {string} [props.className] - Additional CSS classes
 */
export default function VarianceBreakdown({ customers: weeks = [], className }) {
  if (!weeks || weeks.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Variance Breakdown</CardTitle>
          <CardDescription>Weeks with highest prediction variance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            No variance data available
          </div>
        </CardContent>
      </Card>
    )
  }

  // Find max variance for bar scaling
  const maxVariance = Math.max(...weeks.map(w => w.variance_abs || 0))

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Variance Breakdown</CardTitle>
        <CardDescription>
          Weeks with highest prediction variance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-slate-100">
          {weeks.map((week, index) => (
            <VarianceRow
              key={week.id || index}
              week={week}
              maxVariance={maxVariance}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
